import { useEffect, useMemo, useRef, useState } from 'react'

import { DepositTarget } from '@pages/Traders-Room/Deposit/hooks/types'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import type { TradingAccountDetailDto } from '@model/TradingAccountDetailDto'
import type { WalletDto } from '@model/WalletDto'

const safeErrorPayload = (e: any) => {
  const payload: any = { message: e?.message, name: e?.name }
  const r = e?.response
  if (r) payload.response = { status: r?.status, statusText: r?.statusText, data: r?.data }
  if (e?.data) payload.data = e.data
  if (e?.status) payload.status = e.status
  return payload
}

export const useDepositDestination = (params: { target?: DepositTarget; id?: string }) => {
  const { target, id } = params
  const apiClient = useApiClient(ClientApiClient)

  const requestKey = useMemo(() => {
    if (!target || !id) return null
    return `${target}:${id}`
  }, [target, id])

  const [destination, setDestination] = useState<WalletDto | TradingAccountDetailDto | undefined>(
    undefined
  )
  const [destinationLoading, setDestinationLoading] = useState(false)

  const activeRequestRef = useRef(0)
  const lastKeyRef = useRef<string | null>(null)

  useEffect(() => {
    const reqId = ++activeRequestRef.current

    if (!requestKey || !target || !id) {
      lastKeyRef.current = null
      setDestination(undefined)
      setDestinationLoading(false)
      return
    }

    const isSameKey = lastKeyRef.current === requestKey
    lastKeyRef.current = requestKey

    setDestinationLoading(true)

    if (!isSameKey) {
      setDestination(undefined)
    }

    const run = async () => {
      try {
        const res =
          target === 'wallet'
            ? ((await apiClient.getWalletById(id)) as WalletDto)
            : ((await apiClient.getTradingAccountById(id)) as TradingAccountDetailDto)

        if (reqId !== activeRequestRef.current) return
        setDestination(res)
      } catch (e) {
        if (reqId !== activeRequestRef.current) return
        const payload = safeErrorPayload(e)
        setDestination(undefined)
        console.error('DEPOSIT_DESTINATION_FAILED', payload)
      } finally {
        if (reqId === activeRequestRef.current) setDestinationLoading(false)
      }
    }

    void run()

    return () => {
      activeRequestRef.current++
    }
  }, [apiClient, id, requestKey, target])

  const walletIdForProvider = useMemo(() => {
    if (!destination || !target) return ''
    if (target === 'wallet') return (destination as WalletDto).id
    return (destination as TradingAccountDetailDto).wallet?.id ?? ''
  }, [destination, target])

  return { destination, destinationLoading, walletIdForProvider }
}
